import type { ReactNode } from 'react';
import { DateSearchSelect, InputSearch, MultipleSearchSelect, type SelectSearchOption } from './search-inputs';
import type { DataTableColumn, DataTableRenderCellParams, DataTableRowModel } from './types';

export const DEFAULT_BOOLEAN_COLUMN_WIDTH = 80;

export type DataTableColMeta = Pick<DataTableColumn, 'field' | 'type'>;
export type FilterOptions = SelectSearchOption[] | (() => SelectSearchOption[]);

export type DataTableFilter =
  | false
  | {
      type: 'date';
    }
  | {
      type: 'multiple';
      options: FilterOptions;
    }
  | {
      type: 'text';
      after?: ReactNode;
    };

export type DataTableValueGetter<Row extends DataTableRowModel> = (row: Row) => unknown;
export type DataTableValueFormatter<Row extends DataTableRowModel> = (value: unknown, row: Row) => string;
export type DataTableCellRenderer<Row extends DataTableRowModel> = (
  params: DataTableRenderCellParams<Row>
) => ReactNode;

export type DataTableColPreset<Row extends DataTableRowModel> = (
  col: Readonly<DataTableColConfig<Row>>
) => Partial<DataTableColConfig<Row>>;

export type DataTableColPresetInput<Row extends DataTableRowModel> =
  | DataTableColPreset<Row>
  | ReadonlyArray<DataTableColPreset<Row>>;

export type DataTableColConfig<Row extends DataTableRowModel> = {
  field: string;
  headerName: string;
  width?: number;
  columnType?: DataTableColumn['type'];
  align?: DataTableColumn['align'];
  headerAlign?: DataTableColumn['headerAlign'];
  valueGetter?: DataTableValueGetter<Row>;
  valueFormatter?: DataTableValueFormatter<Row>;
  renderCell?: DataTableCellRenderer<Row>;
  renderSearchInput?: DataTableColumn['renderSearchInput'];
  filter?: DataTableFilter;
  nonWritable?: boolean;
  preset?: DataTableColPresetInput<Row>;
};

export type DataTableCol<Row extends DataTableRowModel = DataTableRowModel> = DataTableColConfig<Row>;

export type CheckboxPresetOptions<Row extends DataTableRowModel> = {
  getCheckedValue?: (row: Row) => boolean;
  renderCheckbox: (checked: boolean, row: Row, params: DataTableRenderCellParams<Row>) => ReactNode;
  width?: number;
  columnType?: DataTableColumn['type'];
  align?: DataTableColumn['align'];
  headerAlign?: DataTableColumn['headerAlign'];
};

const getFilterOptions = (options: FilterOptions) => (typeof options === 'function' ? options() : options);

const createSearchInput = (filter?: DataTableFilter): DataTableColumn['renderSearchInput'] | undefined => {
  if (!filter) {
    return undefined;
  }

  switch (filter.type) {
    case 'date':
      return function DateFilterSearchInput(params) {
        return <DateSearchSelect {...params} />;
      };
    case 'multiple':
      return function MultipleFilterSearchInput(params) {
        return <MultipleSearchSelect {...params} options={getFilterOptions(filter.options)} />;
      };
    case 'text':
      return function TextFilterSearchInput(params) {
        return <InputSearch {...params} after={filter.after} />;
      };
  }
};

const resolvePresets = <Row extends DataTableRowModel>(preset?: DataTableColPresetInput<Row>) => {
  if (!preset) {
    return [] as DataTableColPreset<Row>[];
  }

  return Array.isArray(preset) ? [...preset] : [preset];
};

const applyPresets = <Row extends DataTableRowModel>(col: DataTableColConfig<Row>): DataTableColConfig<Row> => {
  return resolvePresets(col.preset).reduce<DataTableColConfig<Row>>((resolvedCol, preset) => {
    const patch = preset(resolvedCol);
    return Object.keys(patch).length > 0 ? { ...resolvedCol, ...patch } : resolvedCol;
  }, col);
};

export const composeDataTablePresets = <Row extends DataTableRowModel>(
  ...presets: Array<DataTableColPreset<Row> | undefined>
): DataTableColPreset<Row> => {
  const activePresets = presets.filter(Boolean) as DataTableColPreset<Row>[];

  return col => applyPresets({ ...col, preset: activePresets });
};

export const createCheckboxPreset = <Row extends DataTableRowModel>({
  getCheckedValue,
  renderCheckbox,
  width,
  columnType = 'boolean',
  align = 'center',
  headerAlign
}: CheckboxPresetOptions<Row>): DataTableColPreset<Row> => {
  return col => {
    const resolveCheckedValue = (row: Row) => {
      if (getCheckedValue) {
        return Boolean(getCheckedValue(row));
      }

      if (col.valueGetter) {
        return Boolean(col.valueGetter(row));
      }

      return Boolean((row as Record<string, unknown> | undefined)?.[col.field]);
    };

    return {
      width: col.width ?? width ?? DEFAULT_BOOLEAN_COLUMN_WIDTH,
      columnType: col.columnType ?? columnType,
      align: col.align ?? align,
      headerAlign: col.headerAlign ?? headerAlign ?? 'center',
      valueGetter: col.valueGetter ?? resolveCheckedValue,
      renderCell: col.renderCell ?? (params => renderCheckbox(resolveCheckedValue(params.row), params.row, params))
    };
  };
};

const createBaseCol = <Row extends DataTableRowModel>({
  field,
  headerName,
  width,
  columnType,
  align,
  headerAlign,
  valueGetter,
  valueFormatter,
  renderCell,
  renderSearchInput,
  filter,
  nonWritable
}: DataTableColConfig<Row>): DataTableColumn => {
  const col: DataTableColumn = {
    field,
    headerName,
    type: columnType ?? (filter && filter.type === 'date' ? 'date' : undefined),
    align,
    headerAlign: headerAlign ?? 'center',
    renderSearchInput: renderSearchInput ?? createSearchInput(filter),
    nonWritable
  };

  if (typeof width === 'number') {
    col.width = width;
  }

  if (valueGetter) {
    col.valueGetter = ((_: unknown, row: Row) => valueGetter(row)) as DataTableColumn['valueGetter'];
  }

  if (valueFormatter) {
    col.valueFormatter = ((value: unknown, row: Row) =>
      valueFormatter(value, row)) as DataTableColumn['valueFormatter'];
  }

  if (renderCell) {
    col.renderCell = renderCell as DataTableColumn['renderCell'];
  }

  return col;
};

const toColumn = <Row extends DataTableRowModel>(col: DataTableCol<Row>): DataTableColumn => {
  const resolvedCol = applyPresets(col);
  return createBaseCol(resolvedCol);
};

export const buildDataTableCols = <Row extends DataTableRowModel>(
  cols: ReadonlyArray<DataTableCol<Row>>
): DataTableColumn[] => cols.map(toColumn);

export const getDataTableColMeta = (cols: DataTableColumn[]): DataTableColMeta[] =>
  cols.map(col => ({
    field: col.field,
    type: col.type
  }));
